import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import closeIcon from "../assets/images/close.png";
import loginImage from "../assets/images/login.png";
import { primaryColor } from "../services/theme_service";
import { sendOtp, verifyOtp, retryOtp } from "../services/otp_service";
import OtpInput from "./components/otp_Input";
import { getUser, registerUser } from "../services/api_service";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import { useCart } from "../context/cart_context";
import { validateEmail } from "../helper/global_helper";

const LoginPage: React.FC = () => {
  const [step, setStep] = useState<"phone" | "otp" | "register">("phone");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(20);
  const [loading, setLoading] = useState(false);
  const [reqId, setReqId] = useState<string | null>(null);
  const { populateCart } = useCart();

  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");


  // Reset state when page loads
  useEffect(() => {
    setStep("phone");
    setOtp("");
    setError("");
    setReqId(null);
    setTimer(20);
    setLoading(false);
    const mobilenumber = localStorage.getItem("mobileNumber");
    setPhone(mobilenumber || "");
    if (mobilenumber) {
      setStep('register');
    }
  }, []);

  // OTP timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (step === "otp" && timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  const handleSendOtp = async () => {
    if (phone.length !== 10) {
      setError("Please enter a valid 10 digit number.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await sendOtp(`91${phone}`);
      if (data.type === "success") {
        setReqId(data.message);
        setStep("otp");
        setTimer(20);
      } else {
        setError(data.message || "Failed to send OTP");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!reqId) return;
    setLoading(true);
    setError("");
    try {
      const data = await verifyOtp(otp, reqId);
      if (data.type === "success") {
        toast("OTP verified successfully!");
        const result = await getUser(phone);
        if (result.status === "new_user") {
          localStorage.setItem("mobileNumber", phone);
          setStep("register");
          return;
        }
        if (result.isRegistered) {
          localStorage.setItem("userId", result.user_id);
        }
        populateCart(result.user_id);
        navigate("/");
      } else {
        setError(data.message || "OTP verification failed");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const register = async () => {
    if(loading) return;
    try {
      setLoading(true);
      setError("");
      const { customer: newCustomer, error } = await registerUser({
        user_name: fullName,
        email_id: email,
        mobile_number: phone,
      });
      setLoading(false);
      if (newCustomer?.user_id) {
              toast("User registered successfully!");
        localStorage.setItem("userId", newCustomer?.user_id?.toString());
        populateCart(newCustomer.user_id);
        navigate("/");
      
      } else if (error?.includes("already exists")) {
        setLoading(false);
        toast(error || "Email/Account already exists");
      } else {
        setLoading(false);
        toast(error || "Something went wrong. Please try again later");
        setError(error || "Something went wrong. Please try again later");
      }
    } catch (e: any) {
      localStorage.removeItem("mobileNumber")
       setError(error || "Something went wrong. Please try again later");
      toast(e?.message || "Session timed out. Please login again");
    }
  };

  const handleRetryOtp = async () => {
    if (!reqId) return;
    setLoading(true);
    setError("");
    try {
      const data = await retryOtp(reqId);
      if (data.type !== "success") setError(data.message || "Retry failed");
      else setTimer(20);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const otpFilled = otp.length === 4;

  const quickLinks = [
    { label: "📜 Terms & Conditions", tab: "T&C" },
    { label: "🚚 Shipping Policy", tab: "Shipping Policy" },
    { label: "📦 Return Policy", tab: "Return Policy" },
    { label: "↩️ Refund Policy", tab: "Refund Policy" },
    { label: "🔒 Privacy Policy", tab: "Privacy Policy" },
  ];

  return isMobile ? (
    // -------- Mobile Layout --------
    <div style={mStyles.page}>
      <div style={mStyles.closeRow}>
        <img src={closeIcon} alt="close" style={mStyles.closeIcon} onClick={() => navigate("/")} />
      </div>

      <div style={mStyles.imageWrapper}>
        <img src={loginImage} alt="preview" style={mStyles.image} />
      </div>

      <div style={mStyles.content}>
        {step !== "register" && <h2 style={mStyles.title}>Welcome to Manthri’s</h2>}

        {step === "register" ? (
          <div
            style={{
              width: "100%",
              maxWidth: 400,
              display: "flex",
              flexDirection: "column",
              gap: 14,
              marginTop: 10,
            }}
          >
            <h3 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>
              Looks like you're new
            </h3>

            <p style={{ fontSize: 20, color: "#666", margin: 0 }}>
              Please enter details to create account
            </p>

            {/* FULL NAME */}
            <input
              type="text"
              placeholder="Full Name *"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: 8,
                border: "1px solid #ccc",
                fontSize: 20,
                outline: "none",
                marginTop: 10
              }}
            />

            {/* EMAIL */}
            <input
              type="email"
              placeholder="Email ID *"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError(validateEmail(e.target.value));
              }}
              style={{
                marginTop: 10,
                width: "100%",
                padding: "12px",
                borderRadius: 8,
                border: emailError ? "1px solid red" : "1px solid #ccc",
                fontSize: 20,
                outline: "none",
              }}
            />

            {emailError && (
              <span style={{ color: "red", fontSize: 13 }}>
                {emailError}
              </span>
            )}

            {/* MOBILE (readonly) */}
            <input
              type="text"
              value={`+91 ${phone}`}
              disabled
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: 8,
                border: "1px solid #eee",
                fontSize: 20,
                background: "#f5f5f5",
                color: "#666",
                marginTop: 10
              }}
            />

            {/* BUTTON */}
          

            <button
              disabled={!fullName || !email || !!emailError}
              onClick={register}
              style={{
                marginLeft: "3%",
                width: "100%",
                padding: "14px",
                borderRadius: 8,
                border: "none",
                fontSize: 20,
                fontWeight: 600,
                color: "#fff",
                marginTop: 18,
                backgroundColor:
                  fullName && email && !emailError ? primaryColor : "#ccc",
                cursor:
                  fullName && email && !emailError ? "pointer" : "not-allowed",
              }}
            >
           {loading ? "Registering..." : "Register"}    
            </button>
          </div>
        ) :

          step === "phone" ? (
            <>
              <div style={mStyles.phoneRow}>
                <div style={mStyles.countryCode}>+91</div>
                <input
                  type="tel"
                  maxLength={10}
                  placeholder="Enter Mobile Number *"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                  style={mStyles.phoneInput}
                />
              </div>
              {error && <span style={mStyles.errorText}>{error}</span>}
              <button onClick={handleSendOtp} style={mStyles.continueButton} disabled={loading}>
                {loading ? "Sending OTP..." : "Continue"}
              </button>
              <div style={mStyles.quickLinksBox}>
                {quickLinks.map((item, i) => (
                  <div
                    key={i}
                    style={mStyles.quickLinkItem}
                    onClick={() => navigate(`/documents?tab=${encodeURIComponent(item.tab)}`)}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <p style={mStyles.infoText}>We have sent you a 4 digit verification code.</p>
              <span style={mStyles.changeNumber} onClick={() => setStep("phone")}>
                Change Number
              </span>
              <OtpInput onOtpFilled={(code: any) => setOtp(code)} />
              {error && <span style={mStyles.errorText}>{error}</span>}
              <button
                onClick={handleVerifyOtp}
                disabled={!otpFilled || loading}
                style={{
                  ...mStyles.verifyButton,
                  backgroundColor: otpFilled ? primaryColor : "#ccc",
                }}
              >
                {loading ? "Verifying..." : "Verify"}
              </button>
              <div style={mStyles.resendRow}>
                <span
                  onClick={handleRetryOtp}
                  style={{
                    ...mStyles.resendText,
                    color: timer > 0 ? "#ccc" : primaryColor,
                  }}
                >
                  Resend
                </span>
                {timer > 0 && <span style={mStyles.timerText}>{timer} seconds</span>}
              </div>
            </>
          )}
      </div>
    </div>
  ) : (
    // -------- Web Layout --------
    <div style={styles.page}>
      <div style={styles.leftContainer}>
        <div style={styles.closeRow}>
          <img src={closeIcon} alt="close" style={styles.closeIcon} onClick={() => navigate("/")} />
        </div>

        <div style={{
          ...styles.centerColumn,
          gap: step === "register" ? 0 : 20,
        }}>

          {step === "register" ? (
            <>
              <div style={styles.registerHeader}>
                <h3 style={styles.registerTitle}>Looks like you're new</h3>
                <p style={styles.registerSubtitle}>
                  Please enter below details to create an account
                </p>
              </div>

              {/* FULL NAME */}
              <div style={styles.registerField}>
                {fullName && (
                  <label style={styles.registerLabel}>Full Name *</label>
                )}
                <input
                  type="text"
                  placeholder="Full Name *"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  style={styles.registerInput}
                />
              </div>

              {/* EMAIL */}
              <div >
                {email && (
                  <label
                    style={{
                      ...styles.registerLabel,
                      color: emailError ? "red" : "#777",
                    }}
                  >
                    Email ID *
                  </label>
                )}
                <input
                  type="email"
                  placeholder="Email ID *"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(validateEmail(e.target.value));
                  }}
                  style={{
                    ...styles.registerInput,
                    border: emailError ? "1px solid red" : "1px solid #ddd",
                  }}
                />
              </div>

              {emailError && (
                <span style={styles.registerErrorText}>{emailError}</span>
              )}

              {/* MOBILE */}
              <div style={{ ...styles.registerField, ...styles.registerMobile }}>
                <label style={styles.registerLabel}>Mobile Number</label>
                <input
                  type="tel"
                  value={`+91 ${phone}`}
                  disabled
                  style={{
                    ...styles.registerInput,
                    background: "#f5f5f5",
                    color: "#666",
                    border: "1px solid #eee",
                  }}
                />
              </div>

              {/* BUTTON */}
              <button
              onClick={register}
                disabled={!fullName || !email || !!emailError}
                style={{
                  ...styles.registerButton,
                  backgroundColor:
                    fullName && email && !emailError ? primaryColor : "#ccc",
                  cursor:
                    fullName && email && !emailError ? "pointer" : "not-allowed",
                }}
              // onClick={handleRegister}
              >
                 {loading ? "Registering..." : "Register"} 
              </button>
            </>
          ) :
            (<>
              <h2 style={styles.title}>Welcome to Manthri’s</h2>
              {step === "phone" ? (
                <>
                  <div style={styles.phoneRow}>
                    <div style={styles.countryCode}>+91</div>
                    <input
                      type="tel"
                      maxLength={10}
                      placeholder="Enter Mobile Number *"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                      style={styles.phoneInput}
                    />
                  </div>
                  {error && <span style={styles.errorText}>{error}</span>}
                  <button onClick={handleSendOtp} style={styles.continueButton} disabled={loading}>
                    {loading ? "Sending OTP..." : "Continue"}
                  </button>
                  <div style={styles.dividerRow}>
                    <hr style={styles.divider} />
                    <span style={styles.dividerText}>Quick Links</span>
                    <hr style={styles.divider} />
                  </div>
                  <div style={styles.quickLinksBox}>
                    {quickLinks.map((item, index) => (
                      <div
                        key={index}
                        style={styles.quickLinkItem}
                        onClick={() =>
                          item.tab
                            ? navigate(`/documents?tab=${encodeURIComponent(item.tab)}`)
                            : null
                        }
                      >
                        <div style={styles.quickLinkIcon}>
                          {item.label.split(" ")[0]} {/* emoji */}
                        </div>
                        <div style={styles.quickLinkText}>
                          {item.label.replace(/^[^\w]+/, "")} {/* text without emoji */}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p style={styles.infoText}>We have sent you a 4 digit verification code to {phone}</p>

                  <span style={styles.changeNumber} onClick={() => setStep("phone")}>
                    Change Number
                  </span>
                  <div style={styles.otpRow}>
                    <OtpInput onOtpFilled={(code: any) => setOtp(code)} />
                  </div>
                  {error && <span style={styles.errorText}>{error}</span>}
                  <button
                    onClick={handleVerifyOtp}
                    disabled={!otpFilled || loading}
                    style={{
                      ...styles.verifyButton,
                      backgroundColor: otpFilled ? primaryColor : "#ccc",
                      cursor: otpFilled ? "pointer" : "not-allowed",
                    }}
                  >
                    {loading ? "Verifying..." : "Verify"}
                  </button>
                  <div style={styles.resendRow}>
                    <span
                      onClick={handleRetryOtp}
                      style={{
                        ...styles.resendText,
                        color: timer > 0 ? "#ccc" : primaryColor,
                      }}
                    >
                      Resend
                    </span>
                    {timer > 0 && <span style={styles.timerText}>{timer} seconds</span>}
                  </div>
                </>
              )}
            </>)}
        </div>
      </div>
      <div style={styles.rightContainer}>
        <img src={loginImage} alt="login" style={styles.loginImage} />
      </div>
    </div>
  );
};

// ---------------- Styles ----------------

const styles: { [key: string]: React.CSSProperties } = {
  page: { display: "flex", height: "100vh", width: "100%", fontFamily: "Arimo, sans-serif" },
  leftContainer: { flex: 1, display: "flex", flexDirection: "column", padding: "20px 40px" },
  closeRow: { display: "flex", justifyContent: "flex-start" },
  closeIcon: { cursor: "pointer", width: 35, height: 35 },
  centerColumn: { flex: 1, display: "flex", flexDirection: "column", gap: 20, maxWidth: 400, margin: "10% 20%" },
  title: { fontSize: 26, fontWeight: 600, letterSpacing: 0.8 },
  phoneRow: { display: "flex", gap: 10 },
  countryCode: { padding: "10px 15px", borderRadius: 8, background: "#f2f2f2", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 500 },
  phoneInput: { padding: "12px 15px", borderRadius: 8, border: "1px solid #ccc", outline: "none", fontSize: 17, width: "400px" },
  errorText: { color: "red", fontSize: 14 },
  continueButton: { width: "100%", padding: "12px", backgroundColor: primaryColor, color: "#fff", border: "none", borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: "pointer" },
  dividerRow: { display: "flex", alignItems: "center", gap: 10, margin: "15px 0 5px" },
  divider: { flex: 1, border: "none", borderTop: "1px solid #ddd" },
  dividerText: { fontSize: 14, color: "#777", fontWeight: 500 },
  quickLinksBox: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 16,
    marginTop: 10
  },
  quickLinkItem: {
    background: "#ffe6f0",
    borderRadius: 12,
    padding: "10px 10px",
    textAlign: "center",
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
  quickLinkIcon: {
    fontSize: 28,
    marginBottom: 8,
  },
  quickLinkText: {
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: 0.7
  },
  infoText: { margin: 0 },
  changeNumber: { color: primaryColor, cursor: "pointer", fontWeight: 500 },
  otpRow: { display: "flex" },
  verifyButton: { width: "100%", padding: "12px", color: "#fff", border: "none", borderRadius: 8, fontSize: 16, fontWeight: 600, marginTop: 10 },
  resendRow: { fontSize: 14, justifyContent: "center", display: "flex", gap: 8 },
  resendText: { textDecoration: "underline", cursor: "pointer" },
  timerText: { marginLeft: 8, color: "#555" },
  rightContainer: { flex: 1, backgroundColor: "#fff", display: "flex", justifyContent: "center", alignItems: "center" },
  loginImage: { width: "100%", height: "96%", objectFit: "cover", borderRadius: 20, marginRight: 15 },
  input: {
    width: "100%",
    padding: "12px",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 16,
  },
  registerHeader: {
    marginBottom: 16,
  },

  registerTitle: {
    fontSize: 22,
    fontWeight: 600,
    marginBottom: 4,
  },

  registerSubtitle: {
    fontSize: 15,
    color: "#666",
  },

  registerField: {
    marginBottom: 16,
  },

  registerMobile: {
    marginTop: 16,
    marginBottom: 30,
  },

  registerLabel: {
    fontSize: 14,
    color: "#777",
    marginBottom: 4,
    display: "block",
  },

  registerInput: {
    width: "100%",
    padding: "12px",
    borderRadius: 8,
    border: "1px solid #ddd",
    fontSize: 17,
    outline: "none",
  },

  registerErrorText: {
    color: "red",
    fontSize: 14,
    marginTop: 6,
    display: "block",
  },

  registerButton: {
    width: "100%",
    padding: "14px",
    borderRadius: 8,
    border: "none",
    fontSize: 15,
    marginLeft: "3%",
    fontWeight: 600,
    color: "#fff",
    transition: "0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

};

const mStyles: { [key: string]: React.CSSProperties } = {
  page: { display: "flex", flexDirection: "column", minHeight: "100vh", fontFamily: "Arimo, sans-serif" },
  closeRow: { display: "flex", justifyContent: "flex-start", padding: "10px" },
  closeIcon: { width: 30, height: 30, cursor: "pointer" },
  imageWrapper: { display: "flex", justifyContent: "center", alignItems: "center", width: "100%" },
  image: { width: "95%", height: "250px", borderRadius: 10, objectFit: "cover" },
  content: { flex: 1, padding: 20, display: "flex", flexDirection: "column", alignItems: "center", gap: 16, overflowY: "auto" },
  title: { fontSize: 22, fontWeight: 600 },
  phoneRow: { display: "flex", gap: 10, width: "100%" },
  countryCode: { padding: "10px 15px", background: "#f2f2f2", borderRadius: 8 },
  phoneInput: { flex: 1, padding: "12px", borderRadius: 8, border: "1px solid #ccc" },
  errorText: { color: "red", fontSize: 14 },
  continueButton: { width: "100%", padding: "12px", backgroundColor: primaryColor, color: "#fff", borderRadius: 8, border: "none", fontSize: 16, fontWeight: 600 },
  quickLinksBox: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 12, marginTop: 10 },
  quickLinkItem: { background: "#ffe6f0", padding: "8px 12px", borderRadius: 8, fontSize: 14, cursor: "pointer" },
  infoText: { fontSize: 14 },
  changeNumber: { color: primaryColor, cursor: "pointer" },
  verifyButton: { width: "100%", padding: "12px", borderRadius: 8, color: "#fff", fontWeight: 600, border: "none" },
  resendRow: { display: "flex", gap: 8, justifyContent: "center" },
  resendText: { textDecoration: "underline", cursor: "pointer" },
  timerText: { fontSize: 14, color: "#555" },
};

export default LoginPage;
